import { ComponentProps, createContext, useContext } from 'react';
import { useParsedRouteParams } from '../hooks';
import { z } from 'zod';

/*
*   Конекст для Page
*/

export type PageContextValue<T> = {
    pageRouteParams: T,
}

function once<TFunction extends (...args: any[]) => any>(fn: TFunction): TFunction {
    let haveResult = false;
    let result: any = null;

    return (function (this: any, ...args: any[]) {
        if (!haveResult) {
            haveResult = true;
            result = fn.apply(this, args);
        }

        return result;
    }) as any;
}

export const createPageContext = once(<T,>() => createContext<PageContextValue<T> | null>(null));

export type PageProviderProps<T extends z.ZodType<any, any, any>> = {
    Page: React.FunctionComponent,
    zodRouteParams: T,
}

export const combineComponents = (...components: React.FunctionComponent<any>[]): React.FunctionComponent<any> => {
    return components.reduce(
        (AccumulatedComponents, CurrentComponent) => {
            return ({ children }: ComponentProps<React.FunctionComponent<any>>): JSX.Element => {
                return (
                    <AccumulatedComponents>
                        <CurrentComponent>{children}</CurrentComponent>
                    </AccumulatedComponents>
                );
            };
        },
        ({ children }) => <>{children}</>,
    );
};


export const PageProvider = <T extends z.ZodType<any, any, any>>({ Page, zodRouteParams }: PageProviderProps<T>): JSX.Element => {

    const PageContext = createPageContext<T>();
    const pageRouteParams = useParsedRouteParams(zodRouteParams);

    if (!pageRouteParams.isValid)
        return <></>;

    return (
        <PageContext.Provider
            value={{
                pageRouteParams,
            }}
        >
            <Page />
        </PageContext.Provider>
    );
};

export const usePage = <T,>(): PageContextValue<T> => {
    const context = useContext(createPageContext<T>());

    if (context == null) {
        throw new Error('No PageContext provided');
    }

    return context;
};

